import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Printer, History, Network, ArrowLeftRight, Wrench, UserPlus, LifeBuoy, Ticket } from "lucide-react";
import camApi, { CAM_STATUSES, STATUS_META } from "@/lib/camApi";
import { useCam } from "./ctx";
import { qrDataUrl, printLabels } from "./qr";
import RaiseTicketDialog from "./RaiseTicketDialog";

function Pill({ status }) {
  const m = STATUS_META[status] || { label: status, color: "#64748b" };
  return <span className="inline-flex items-center gap-1.5 text-xs font-semibold"><span className="h-2 w-2 rounded-full" style={{ background: m.color }} />{m.label}</span>;
}

export default function AssetDrawer({ assetId, open, onOpenChange, onChanged }) {
  const { context, categories, tree, catName, labName, roomName, deptName } = useCam();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("overview");
  const [qr, setQr] = useState(null);
  const [ticketOpen, setTicketOpen] = useState(false);
  const canWrite = context?.cam_admin || context?.is_store;

  const load = async () => {
    if (!assetId) return;
    setLoading(true);
    try {
      const r = await camApi.asset(assetId);
      setData(r);
      setQr(await qrDataUrl(r.asset?.asset_tag, { width: 200 }));
    } catch (e) { toast.error(e.message); onOpenChange(false); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    if (open && assetId) { setTab("overview"); load(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, assetId]);

  const a = data?.asset;
  const cat = a ? categories.find((c) => c.id === a.category_id) : null;
  const sub = cat?.subcategories?.find((s) => s.id === a?.subcategory_id) || null;
  const visibleFields = (cat?.fields || []).filter((fld) => !fld.subcategory_id || fld.subcategory_id === a?.subcategory_id);
  const locStr = a ? [roomName(a.room_id), labName(a.lab_id), data?.seat?.label ? `Seat ${data.seat.label}` : null].filter(Boolean).join(" · ") : "";

  const changeStatus = async (status) => {
    try { await camApi.setStatus(a.id, status); toast.success(`Status set to ${STATUS_META[status]?.label || status}`); await load(); onChanged?.(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto touch-scroll p-0">
        {loading && !a ? (
          <div className="p-8 text-sm text-muted-foreground">Loading…</div>
        ) : a ? (
          <div>
            <SheetHeader className="p-5 border-b sticky top-0 bg-background/95 backdrop-blur z-10">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-mono text-xs text-muted-foreground">{a.asset_tag}</div>
                  <SheetTitle className="font-display text-xl tracking-tighter truncate">{a.name}</SheetTitle>
                  <div className="mt-1"><Pill status={a.status} /></div>
                </div>
                {qr && <img src={qr} alt={a.asset_tag} className="h-16 w-16 rounded-lg border" />}
              </div>
              <div className="flex flex-wrap gap-1.5 pt-1">
                <Button size="sm" className="h-8 bg-cobalt-500 hover:bg-cobalt-600" onClick={() => setTicketOpen(true)}><LifeBuoy className="h-3.5 w-3.5 mr-1" />Raise ticket</Button>
                <Button size="sm" variant="outline" className="h-8" onClick={() => printLabels(a)}><Printer className="h-3.5 w-3.5 mr-1" />Label</Button>
                {canWrite && <TransferButton asset={a} tree={tree} onDone={() => { load(); onChanged?.(); }} />}
                {(canWrite || context?.is_maintenance) && <WorkOrderButton asset={a} onDone={() => { load(); onChanged?.(); }} />}
                {canWrite && <AssignButton asset={a} tree={tree} onDone={() => { load(); onChanged?.(); }} />}
              </div>
            </SheetHeader>

            <div className="flex gap-1 px-5 pt-4 border-b">
              {[["overview", "Overview"], ["network", "Network"], ["tickets", "Tickets"], ["history", "History"]].map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)}
                  className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${tab === k ? "border-cobalt-500 text-foreground" : "border-transparent text-muted-foreground"}`}>
                  {k === "history" && <History className="h-3.5 w-3.5 inline mr-1" />}
                  {k === "network" && <Network className="h-3.5 w-3.5 inline mr-1" />}
                  {k === "tickets" && <Ticket className="h-3.5 w-3.5 inline mr-1" />}
                  {l}{k === "tickets" && data.tickets?.length ? ` (${data.tickets.length})` : ""}
                </button>
              ))}
            </div>

            <div className="p-5 space-y-5">
              {tab === "overview" && (
                <>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                    <Field label="Category" value={sub ? `${cat?.name || catName(a.category_id)} · ${sub.name}` : (cat?.name || catName(a.category_id))} />
                    <Field label="Condition" value={a.condition} />
                    <Field label="Brand / Model" value={[a.brand, a.model].filter(Boolean).join(" ") || "—"} />
                    <Field label="Serial no." value={a.serial_number || "—"} mono />
                    <Field label="Owner dept" value={deptName(a.owner_department_id) || "—"} />
                    <Field label="Location" value={[roomName(a.room_id), labName(a.lab_id)].filter(Boolean).join(" · ") || "—"} />
                    <Field label="Purchase" value={a.purchase_date ? new Date(a.purchase_date).toLocaleDateString() : "—"} />
                    <Field label="Cost" value={a.purchase_cost != null ? `₹${Number(a.purchase_cost).toLocaleString("en-IN")}` : "—"} />
                    <Field label="Vendor" value={a.vendor || "—"} />
                    <Field label="Warranty" value={a.warranty_expiry ? new Date(a.warranty_expiry).toLocaleDateString() : "—"} />
                  </dl>

                  {visibleFields.length > 0 && (
                    <div>
                      <div className="overline mb-2">Specifications</div>
                      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                        {visibleFields.map((f) => (
                          <Field key={f.key} label={f.label} value={fmtField(a.custom_fields?.[f.key], f)} />
                        ))}
                      </dl>
                    </div>
                  )}

                  {canWrite && (
                    <div>
                      <Label className="text-xs">Change status</Label>
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {CAM_STATUSES.map((s) => (
                          <button key={s} onClick={() => s !== a.status && changeStatus(s)} disabled={s === a.status}
                            className={`text-xs rounded-full px-2.5 py-1 border ${s === a.status ? "bg-zinc-900 text-white border-zinc-900 cursor-default" : "hover:bg-black/5"}`}>
                            {STATUS_META[s]?.label || s}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {tab === "network" && <NetworkPanel key={a?.id || "none"} asset={a} network={data.network} canWrite={canWrite} onDone={load} />}

              {tab === "tickets" && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-muted-foreground">Requests raised for this asset flow through the normal helpdesk — routed, tracked and SLA'd like any ticket.</p>
                    <Button size="sm" variant="outline" className="shrink-0" onClick={() => setTicketOpen(true)}><LifeBuoy className="h-3.5 w-3.5 mr-1" />New</Button>
                  </div>
                  {data.tickets?.length ? data.tickets.map((t) => (
                    <div key={t.id} className="flex items-center gap-3 py-2.5 border-b last:border-0">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm truncate">{t.title}</div>
                        <div className="text-[11px] text-muted-foreground">{t.ticket_no} · {new Date(t.created_at).toLocaleDateString()}</div>
                      </div>
                      <span className="text-xs capitalize rounded-full px-2 py-0.5 bg-black/5 dark:bg-white/10">{String(t.status).replace(/_/g, " ")}</span>
                    </div>
                  )) : <p className="text-sm text-muted-foreground">No tickets yet for this asset.</p>}
                </div>
              )}

              {tab === "history" && (
                <div className="space-y-0">
                  {data.history?.length ? data.history.map((h) => (
                    <div key={h.id} className="flex gap-3 py-2.5 border-b last:border-0">
                      <div className="mt-1 h-2 w-2 rounded-full bg-cobalt-500 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm capitalize">{h.event_type.replace(/_/g, " ")}</div>
                        {h.detail && <div className="text-xs text-muted-foreground">{h.detail}</div>}
                        <div className="text-[11px] text-muted-foreground/70 mt-0.5">
                          {h.actor?.name ? `${h.actor.name} · ` : ""}{new Date(h.created_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  )) : <p className="text-sm text-muted-foreground">No history yet.</p>}
                </div>
              )}
            </div>
          </div>
        ) : null}
        {a && <RaiseTicketDialog asset={a} location={locStr} open={ticketOpen} onClose={() => setTicketOpen(false)} onRaised={() => { load(); onChanged?.(); }} />}
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, value, mono }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className={`truncate ${mono ? "text-mono text-xs" : ""}`}>{value}</dd>
    </div>
  );
}
function fmtField(v, f) {
  if (v === undefined || v === null || v === "") return "—";
  if (f.data_type === "boolean") return v ? "Yes" : "No";
  return String(v);
}

function NetworkPanel({ asset, network, canWrite, onDone }) {
  const FIELDS = [
    ["server_room", "Server room"], ["rack_number", "Rack"], ["rack_unit", "Rack unit"], ["patch_panel", "Patch panel"],
    ["switch_port", "Switch port"], ["vlan", "VLAN"], ["ip_address", "IP address"], ["mac_address", "MAC address"],
    ["hostname", "Hostname"], ["firmware", "Firmware"],
  ];
  const [f, setF] = useState(() => { const o = {}; FIELDS.forEach(([k]) => (o[k] = network?.[k] || "")); return o; });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await camApi.saveNetwork(asset.id, f); toast.success("Network details saved"); onDone?.(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">Optional networking details — useful for switches, servers, and any rack-mounted device.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {FIELDS.map(([k, l]) => (
          <div key={k}>
            <Label className="text-xs">{l}</Label>
            <Input className="h-9 mt-1 text-mono text-xs" value={f[k]} disabled={!canWrite} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
          </div>
        ))}
      </div>
      {canWrite && <Button size="sm" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save network details"}</Button>}
    </div>
  );
}

function TransferButton({ asset, tree, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ to_room_id: "", to_lab_id: "", to_department_id: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await camApi.requestTransfer({ asset_id: asset.id, ...f, to_room_id: f.to_room_id || null, to_lab_id: f.to_lab_id || null, to_department_id: f.to_department_id || null });
      toast.success("Transfer requested — pending approval"); setOpen(false); onDone?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  if (!open) return <Button size="sm" variant="outline" className="h-8" onClick={() => setOpen(true)}><ArrowLeftRight className="h-3.5 w-3.5 mr-1" />Transfer</Button>;
  return (
    <div className="w-full mt-2 rounded-xl border p-3 space-y-2 bg-white/50">
      <div className="text-xs font-medium">Request relocation / handover</div>
      <div className="grid grid-cols-2 gap-2">
        <PickerSelect placeholder="To room" value={f.to_room_id} onChange={(v) => setF({ ...f, to_room_id: v })} options={tree.rooms.map((r) => ({ value: String(r.id), label: r.name }))} />
        <PickerSelect placeholder="To lab" value={f.to_lab_id} onChange={(v) => setF({ ...f, to_lab_id: v })} options={tree.labs.map((l) => ({ value: String(l.id), label: l.name }))} />
        <PickerSelect placeholder="To department" value={f.to_department_id} onChange={(v) => setF({ ...f, to_department_id: v })} options={tree.departments.map((d) => ({ value: String(d.id), label: d.name }))} className="col-span-2" />
      </div>
      <Textarea placeholder="Reason (optional)" className="text-sm" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} />
      <div className="flex gap-2"><Button size="sm" onClick={submit} disabled={busy}>{busy ? "…" : "Request"}</Button><Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button></div>
    </div>
  );
}

function WorkOrderButton({ asset, onDone }) {
  const [open, setOpen] = useState(false);
  const [complaint, setComplaint] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!complaint.trim()) return;
    setBusy(true);
    try { await camApi.createRepair({ asset_id: asset.id, complaint }); toast.success("Work order raised"); setOpen(false); setComplaint(""); onDone?.(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  if (!open) return <Button size="sm" variant="outline" className="h-8" onClick={() => setOpen(true)}><Wrench className="h-3.5 w-3.5 mr-1" />Report fault</Button>;
  return (
    <div className="w-full mt-2 rounded-xl border p-3 space-y-2 bg-white/50">
      <div className="text-xs font-medium">Raise a work order</div>
      <Textarea placeholder="What's wrong with this asset?" className="text-sm" value={complaint} onChange={(e) => setComplaint(e.target.value)} />
      <div className="flex gap-2"><Button size="sm" onClick={submit} disabled={busy}>{busy ? "…" : "Raise"}</Button><Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button></div>
    </div>
  );
}

function AssignButton({ asset, tree, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ email: "", employee_code: "", assigned_room_id: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await camApi.assign(asset.id, { email: f.email || null, employee_code: f.employee_code || null, assigned_room_id: f.assigned_room_id || null });
      toast.success("Assignment updated"); setOpen(false); onDone?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  if (!open) return <Button size="sm" variant="outline" className="h-8" onClick={() => setOpen(true)}><UserPlus className="h-3.5 w-3.5 mr-1" />Assign</Button>;
  return (
    <div className="w-full mt-2 rounded-xl border p-3 space-y-2 bg-white/50">
      <div className="text-xs font-medium">Assign to a person (by email) or a room</div>
      <Input placeholder="Custodian email" className="h-9 text-sm" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
      <div className="grid grid-cols-2 gap-2">
        <Input placeholder="Employee / staff ID" className="h-9 text-sm" value={f.employee_code} onChange={(e) => setF({ ...f, employee_code: e.target.value })} />
        <PickerSelect placeholder="Or a room" value={f.assigned_room_id} onChange={(v) => setF({ ...f, assigned_room_id: v })} options={tree.rooms.map((r) => ({ value: String(r.id), label: r.name }))} />
      </div>
      <div className="flex gap-2"><Button size="sm" onClick={submit} disabled={busy}>{busy ? "…" : "Assign"}</Button><Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button></div>
    </div>
  );
}

export function PickerSelect({ placeholder, value, onChange, options, className }) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger className={`h-9 text-sm ${className || ""}`}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}
